import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import useSWR, { mutate as mutateGlobalCache, useSWRConfig } from 'swr'

import { chatRoomsApi, knowledgeThreadsApi, recentConversationsApi } from '@/lib/api'
import {
  RAIL_CONVERSATIONS_KEY,
  revalidateConversationLists,
} from '@/lib/chat/revalidateConversationLists'

import { KnowledgeThreadListItem } from '@/lib/api'
import { revalidateRail } from '@/lib/chat/revalidateRail'
import { useStudyArtifactAttention } from '@/lib/hooks/use-study-artifact-attention'
import type { StudyArtifactAttentionReceipt } from '@digital-twins/shared-types'

import {
  ACTIVE_EXPERIMENT_STATUSES,
  compareRecentAt,
  RecentConversationItem,
  RecentOpenKind,
  RecentRoomItem,
  RecentStudyItem,
  RecentThreadItem,
} from '@digital-twins/shared-types'

/**
 * First page is large enough to fill a tall expanded rail with slack. Further
 * pages load when the sentinel hits the scroller, never as a client slice of
 * the full list.
 */
export const SIDEBAR_THREADS_PAGE_SIZE = 30

/**
 * The SWR key backing the rail's data. Exported so other surfaces (lobby
 * rename/delete, thread create, the open thread page) can patch the shared
 * cache without a refetch. The rail's own fetcher has a 30s dedupe window, so
 * a new thread would otherwise stay missing until refresh.
 */
export const SIDEBAR_THREADS_KEY = RAIL_CONVERSATIONS_KEY

/**
 * Accumulated `GET /recent-conversations` pages: knowledge threads and started
 * chat rooms in one list. The key name predates rooms and is kept so every
 * surface that patches the rail keeps working.
 */
export interface SidebarThreadsCache {
  items: Array<RecentConversationItem>
  nextCursor: string | null
}

/** Rail identity: a thread and a room may share a token string, never a key. */
function conversationKey(item: RecentConversationItem): string {
  return `${item.kind}:${item.token}`
}

function isThreadItem(item: RecentConversationItem): item is RecentThreadItem {
  return item.kind === 'thread'
}

const RAIL_THREAD_SCOPE: KnowledgeThreadListItem['scope'] = {
  mode: 'all',
  documentTokens: [],
  collectionTokens: [],
}

/**
 * Rail row -> the list-item shape the thread surfaces (ThreadsPage, Home Lab,
 * thread detail) already patch with. Only `token`, `title`, `pinned`,
 * `isOwner` and the activity time round-trip; the rest are inert defaults the
 * rail never reads.
 */
function toKnowledgeThread(item: RecentThreadItem): KnowledgeThreadListItem {
  return {
    token: item.token,
    title: item.title,
    visibility: '',
    language: null,
    scope: RAIL_THREAD_SCOPE,
    lastMessageAt: item.lastActivityAt,
    lastMessagePreview: null,
    messageCount: 0,
    citationCount: 0,
    isOwner: item.isOwner,
    isNew: item.isNew === true,
    pinned: item.pinned,
    createdAt: item.lastActivityAt,
    updatedAt: item.lastActivityAt,
  }
}

/** A thread list item as a rail row. Absent `isOwner` reads as owned, as it always has here. */
export function toRecentThreadItem(thread: KnowledgeThreadListItem): RecentThreadItem {
  return {
    kind: 'thread',
    token: thread.token,
    title: thread.title,
    lastActivityAt: thread.lastMessageAt || thread.updatedAt || thread.createdAt,
    // Client-built rows have no open time: sort by activity until a bump says otherwise.
    recentAt: thread.lastMessageAt || thread.updatedAt || thread.createdAt,
    pinned: thread.pinned === true,
    isOwner: thread.isOwner !== false,
    // An opened thread's view says false, so the open itself clears the dot.
    isNew: thread.isNew === true,
  }
}

/**
 * Applies a thread-list transform to the rail cache. Adapter kept for the
 * surfaces that patch threads (rename, pin, delete, upsert): `mapThreads` sees
 * only the cached threads, as `KnowledgeThreadListItem`s; rooms pass through
 * untouched. A surviving thread keeps its slot, a new one goes to the front.
 */
export function mapSidebarThreadsCache(
  current: SidebarThreadsCache | undefined,
  mapThreads: (
    threads: Array<KnowledgeThreadListItem>,
  ) => Array<KnowledgeThreadListItem>,
): SidebarThreadsCache | undefined {
  if (!current || !Array.isArray(current.items)) {
    return current
  }
  const mapped = mapThreads(current.items.filter(isThreadItem).map(toKnowledgeThread)).map(
    toRecentThreadItem,
  )
  const cachedByToken = new Map(
    current.items.filter(isThreadItem).map((thread) => [thread.token, thread]),
  )
  // A patch must not demote a row the user opened: keep the later of the cached
  // sort key and the new activity time (D8).
  const mappedByToken = new Map(
    mapped.map((thread) => {
      const cachedRecentAt = cachedByToken.get(thread.token)?.recentAt
      const recentAt =
        cachedRecentAt && compareRecentAt(cachedRecentAt, thread.lastActivityAt) > 0
          ? cachedRecentAt
          : thread.lastActivityAt
      return [thread.token, { ...thread, recentAt }] as const
    }),
  )
  const placed = new Set<string>()
  const kept: Array<RecentConversationItem> = []
  for (const item of current.items) {
    if (!isThreadItem(item)) {
      kept.push(item)
      continue
    }
    const next = mappedByToken.get(item.token)
    if (next && !placed.has(next.token)) {
      kept.push(next)
      placed.add(next.token)
    }
  }
  const added = mapped
    .filter((thread) => !placed.has(thread.token))
    .map((thread) => mappedByToken.get(thread.token) ?? thread)
  return { ...current, items: [...added, ...kept] }
}

/**
 * Inserts or replaces a thread at the front of the already-loaded rail cache.
 *
 * Leaves an unloaded cache (`undefined`) alone: seeding a one-item page would
 * win over the in-flight first fetch and hide every older thread. After that
 * fetch lands, create/open call this again and the row appears.
 *
 * Cache scope: accumulated pages for this account's rail (`SIDEBAR_THREADS_KEY`).
 */
export function applySidebarThreadUpsert(
  current: SidebarThreadsCache | undefined,
  thread: KnowledgeThreadListItem,
): SidebarThreadsCache | undefined {
  return mapSidebarThreadsCache(current, (threads) => [
    thread,
    ...threads.filter((item) => item.token !== thread.token),
  ])
}

/** Patches `SIDEBAR_THREADS_KEY` so a newly created or opened thread shows in the rail. */
export function upsertSidebarThread(thread: KnowledgeThreadListItem): void {
  void mutateGlobalCache(
    SIDEBAR_THREADS_KEY,
    (current: SidebarThreadsCache | undefined) =>
      applySidebarThreadUpsert(current, thread),
    { revalidate: false },
  )
}

/**
 * Inserts or replaces a room at the front of the already-loaded rail cache.
 * Same unloaded-cache rule as `applySidebarThreadUpsert`.
 *
 * Cache scope: accumulated pages for this account's rail (`SIDEBAR_THREADS_KEY`).
 */
export function applySidebarRoomUpsert(
  current: SidebarThreadsCache | undefined,
  room: RecentRoomItem,
): SidebarThreadsCache | undefined {
  if (!current || !Array.isArray(current.items)) {
    return current
  }
  const key = conversationKey(room)
  return {
    ...current,
    items: [room, ...current.items.filter((item) => conversationKey(item) !== key)],
  }
}

/**
 * Patches `SIDEBAR_THREADS_KEY` once a room has a user message (the server
 * lists started rooms only, so never on create).
 */
export function upsertSidebarRoom(room: RecentRoomItem): void {
  // Best effort: a failed cache patch must never reject unhandled.
  Promise.resolve(
    mutateGlobalCache(
      SIDEBAR_THREADS_KEY,
      (current: SidebarThreadsCache | undefined) => applySidebarRoomUpsert(current, room),
      { revalidate: false },
    ),
  ).catch(() => undefined)
}

/** Patches a cached room's title (live auto-title), no refetch. */
export function patchSidebarRoomTitle(token: string, title: string): void {
  Promise.resolve(
    mutateGlobalCache(
      SIDEBAR_THREADS_KEY,
      (current: SidebarThreadsCache | undefined) => patchRoomInCache(current, token, { title }),
      { revalidate: false },
    ),
  ).catch(() => undefined)
}

function patchRoomInCache(
  current: SidebarThreadsCache | undefined,
  token: string,
  patch: Partial<Pick<RecentRoomItem, 'title'>>,
): SidebarThreadsCache | undefined {
  if (!current || !Array.isArray(current.items)) {
    return current
  }
  return {
    ...current,
    items: current.items.map((item) =>
      item.kind === 'room' && item.token === token ? { ...item, ...patch } : item,
    ),
  }
}

function removeFromCache(
  current: SidebarThreadsCache | undefined,
  kind: RecentConversationItem['kind'],
  token: string,
): SidebarThreadsCache | undefined {
  if (!current || !Array.isArray(current.items)) {
    return current
  }
  return {
    ...current,
    items: current.items.filter((item) => !(item.kind === kind && item.token === token)),
  }
}

/** Rail kind a recorded open belongs to: experiments and drafts are both studies. */
function railKindForOpen(kind: RecentOpenKind): RecentConversationItem['kind'] {
  return kind === 'experiment' || kind === 'draft' ? 'study' : kind
}

function isOpenedRow(item: RecentConversationItem, kind: RecentOpenKind, token: string): boolean {
  if (item.kind !== railKindForOpen(kind) || item.token !== token) {
    return false
  }
  return item.kind !== 'study' || item.studyKind === kind
}

/**
 * Moves an opened row to the front by raising its sort key. Order is applied at
 * read time, so only `recentAt` changes; the key never goes backwards. Returns
 * the cache untouched when the row is not loaded.
 */
export function applyRailBump(
  current: SidebarThreadsCache | undefined,
  kind: RecentOpenKind,
  token: string,
  recentAt: string,
): SidebarThreadsCache | undefined {
  if (!current || !Array.isArray(current.items)) {
    return current
  }
  return {
    ...current,
    items: current.items.map((item) => {
      if (!isOpenedRow(item, kind, token)) {
        return item
      }
      const keep = item.recentAt && compareRecentAt(item.recentAt, recentAt) >= 0
      return keep ? item : { ...item, recentAt }
    }),
  }
}

/**
 * Raises the opened row's sort key in the rail cache after a successful open,
 * no refetch. A visible study that is not in the loaded rail yet has no row to
 * move, so the rail is revalidated instead.
 */
export function bumpRailItem(kind: RecentOpenKind, token: string, recentAt: string): void {
  let hasRow = false
  Promise.resolve(
    mutateGlobalCache(
      SIDEBAR_THREADS_KEY,
      (current: SidebarThreadsCache | undefined) => {
        hasRow =
          Array.isArray(current?.items) &&
          (current?.items ?? []).some((item) => isOpenedRow(item, kind, token))
        return applyRailBump(current, kind, token, recentAt)
      },
      { revalidate: false },
    ),
  )
    .then(() => {
      if (!hasRow) {
        revalidateRail()
      }
    })
    .catch(() => undefined)
}

export interface SidebarThreadItem {
  /** Absent reads as a thread (rows built before rooms joined the rail). */
  kind?: 'thread'
  token: string
  title: string
  isRunning: boolean
  /**
   * Whether this account owns the thread. The rail now lists org-visible
   * threads a colleague shared, and rename / delete / pin are owner-only on
   * the server, so the row menu is hidden on somebody else's row rather than
   * offering actions that 404.
   *
   * Optional, and absence means "treat as ours": only an explicit `false`
   * takes the menu away, so a list response written before the flag existed
   * keeps its own rows actionable.
   */
  isOwner?: boolean
  /** Owner pin. Pinned rows sort ahead of recency. Absent reads as unpinned. */
  pinned: boolean
  /**
   * A teammate's shared thread not opened since it was shared: the row shows
   * a small dot until it is opened. Absent reads as not new.
   */
  isNew?: boolean
  /** Pending persisted artifact revision; opening the thread does not clear it. */
  attention?: StudyArtifactAttentionReceipt
}

/**
 * A started chat room in the rail. `title` stays null when the room has none:
 * the section localizes the fallback (focus group size, or the twin's name
 * for a 1:1). Rooms are always the caller's own.
 */
export interface SidebarRoomItem {
  kind: 'room'
  token: string
  title: string | null
  roomType: RecentRoomItem['roomType']
  participantCount: number
  firstParticipantName: string | null
  /** Caller pin. Pinned rows sort ahead of recency. */
  pinned: boolean
}

/**
 * A launched study or questionnaire draft in the rail. `title` stays null when
 * untitled: the section localizes the fallback.
 */
export interface SidebarStudyItem {
  kind: 'study'
  token: string
  title: string | null
  studyKind: RecentStudyItem['studyKind']
  presentation?: RecentStudyItem['presentation']
  threadOrigin?: RecentStudyItem['threadOrigin']
  draftRevision?: number
  goalId?: string | null
  status: string
  /** True for an experiment still processing or running. */
  isRunning: boolean
  /** Caller pin. Pinned rows sort ahead of recency. */
  pinned: boolean
}

export type SidebarConversationItem = SidebarThreadItem | SidebarRoomItem | SidebarStudyItem

export interface UseSidebarThreadsOptions {
  /**
   * When false the hook holds a null SWR key and issues no request, while still
   * returning a well-formed empty result.
   *
   * No production call site passes it today. `Sidebar` used to, to decide
   * whether the Threads nav entry was needed while the rail itself was hidden;
   * that entry is gone, and `SidebarThreadsSection` — the sole caller left —
   * is simply not mounted where there should be no request. The option stays
   * for the next surface that needs the hook mounted but quiet.
   */
  enabled?: boolean
}

export interface SidebarThreadsResult {
  /** Rail rows, pinned first then newest activity. */
  threads: Array<SidebarConversationItem>
  isLoading: boolean
  isLoadingMore: boolean
  /** True when the server still has a page after the accumulated list. */
  hasMore: boolean
  loadMore: () => Promise<void>
  /** Persists a rename and patches the shared cache, no refetch. */
  renameThread: (token: string, title: string) => Promise<void>
  /** Persists a pin/unpin and patches the shared cache, no refetch. */
  pinThread: (token: string, pinned: boolean) => Promise<void>
  /** Soft-deletes and drops the row from the shared cache, no refetch. */
  deleteThread: (token: string) => Promise<void>
  /** Persists a room rename, patches the rail and revalidates the other conversation lists. */
  renameRoom: (token: string, title: string) => Promise<void>
  /** Deletes a room, drops its row and revalidates the other conversation lists. */
  deleteRoom: (token: string) => Promise<void>
  /** Persists a room pin and patches the row, no refetch. */
  pinRoom: (token: string, pinned: boolean) => Promise<void>
  /** Persists a study pin and patches the row, no refetch. */
  pinStudy: (
    token: string,
    studyKind: RecentStudyItem['studyKind'],
    pinned: boolean,
  ) => Promise<void>
}

/**
 * Threads with work in flight right now.
 *
 * There is no server field for this — "running" is a live client fact owned by
 * whichever surface is driving the stream. Holding it in a module-level store
 * (rather than React state inside the thread page) is what lets the rail keep
 * showing the dot after the user navigates away from that thread. Step 7.1
 * (workflow run transport) is the intended caller.
 */
let runningTokens: ReadonlyArray<string> = []
const runningListeners = new Set<() => void>()

function emitRunningChange(): void {
  for (const listener of runningListeners) {
    listener()
  }
}

export function markSidebarThreadRunning(token: string, isRunning: boolean): void {
  const alreadyRunning = runningTokens.includes(token)
  if (alreadyRunning === isRunning) {
    return
  }
  runningTokens = isRunning
    ? [...runningTokens, token]
    : runningTokens.filter((candidate) => candidate !== token)
  emitRunningChange()
}

/** Clears every running marker. Test seam, so one spec cannot leak into the next. */
export function resetSidebarRunningThreads(): void {
  if (runningTokens.length === 0) {
    return
  }
  runningTokens = []
  emitRunningChange()
}

function subscribeRunning(listener: () => void): () => void {
  runningListeners.add(listener)
  return () => {
    runningListeners.delete(listener)
  }
}

function getRunningSnapshot(): ReadonlyArray<string> {
  return runningTokens
}

/**
 * Newest first on the sort key (`recentAt`, falling back to the display
 * activity time for rows that have none). Pages already arrive in that order
 * from the server; this keeps a locally bumped row from jumping back. Uses
 * `compareRecentAt`, never bare `Date.parse`, so server keys (microseconds) and
 * client `toISOString()` values order together.
 */
function compareConversationRecency(
  left: RecentConversationItem,
  right: RecentConversationItem,
): number {
  return compareRecentAt(right.recentAt ?? right.lastActivityAt, left.recentAt ?? left.lastActivityAt)
}

function isActiveExperimentStatus(status: string): boolean {
  return (ACTIVE_EXPERIMENT_STATUSES as ReadonlyArray<string>).includes(status)
}

function isPinnedItem(item: RecentConversationItem): boolean {
  return item.pinned === true
}

/** Appends a page, dropping any row already loaded (same kind and token). */
function mergeConversations(
  existing: Array<RecentConversationItem>,
  incoming: Array<RecentConversationItem>,
): Array<RecentConversationItem> {
  const seen = new Set(existing.map(conversationKey))
  const appended: Array<RecentConversationItem> = []
  for (const item of incoming) {
    const key = conversationKey(item)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    appended.push(item)
  }
  return [...existing, ...appended]
}

async function fetchSidebarThreadsPage(): Promise<SidebarThreadsCache> {
  const page = await recentConversationsApi.listPage({ limit: SIDEBAR_THREADS_PAGE_SIZE })
  return { items: mergeConversations([], page.items ?? []), nextCursor: page.nextCursor }
}

/**
 * The sidebar's live threads list, called by `SidebarThreadsSection` alone.
 * Mounted only where that section renders, so a collapsed rail, the settings
 * pages, guest mode and Assistant-off orgs issue no request at all.
 *
 * Cache scope: accumulated pages for this account's rail (`SIDEBAR_THREADS_KEY`).
 */
export function useSidebarThreads(options: UseSidebarThreadsOptions = {}): SidebarThreadsResult {
  const { enabled = true } = options
  const { items: attention } = useStudyArtifactAttention(undefined, { enabled })
  const running = useSyncExternalStore(subscribeRunning, getRunningSnapshot, getRunningSnapshot)
  const { mutate: mutateScopedCache } = useSWRConfig()
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const loadingMoreRef = useRef(false)

  const { data, isLoading, mutate } = useSWR(
    enabled ? SIDEBAR_THREADS_KEY : null,
    fetchSidebarThreadsPage,
    {
      dedupingInterval: 30_000,
      keepPreviousData: true,
    },
  )

  const pageRef = useRef(data)
  pageRef.current = data

  const threads = useMemo((): Array<SidebarConversationItem> => {
    const runningSet = new Set(running)
    // A cache page written in the pre-rooms shape (`threads`) has no `items`.
    const loaded = data?.items
    const items = Array.isArray(loaded) ? loaded : []
    return [...items]
      .sort((left, right) => {
        const leftPinned = isPinnedItem(left)
        const rightPinned = isPinnedItem(right)
        if (leftPinned !== rightPinned) {
          return leftPinned ? -1 : 1
        }
        return compareConversationRecency(left, right)
      })
      .map((item): SidebarConversationItem => {
        if (item.kind === 'study') {
          return {
            kind: 'study',
            token: item.token,
            title: item.title?.trim() || null,
            studyKind: item.studyKind,
            ...(item.presentation === 'quick' ? {presentation: item.presentation, ...(item.threadOrigin ? {threadOrigin: item.threadOrigin} : {}),
              ...(item.draftRevision !== undefined ? {draftRevision: item.draftRevision} : {})} : {}),
            goalId: item.goalId ?? null,
            status: item.status,
            isRunning: item.studyKind === 'experiment' && isActiveExperimentStatus(item.status),
            pinned: item.pinned === true,
          }
        }
        if (item.kind === 'room') {
          return {
            kind: 'room',
            token: item.token,
            title: item.title?.trim() || null,
            roomType: item.roomType,
            participantCount: item.participantCount,
            firstParticipantName: item.firstParticipantName?.trim() || null,
            pinned: item.pinned === true,
          }
        }
        return {
          kind: 'thread',
          token: item.token,
          title: item.title?.trim() || 'Untitled',
          isRunning: runningSet.has(item.token),
          // Absent (a cache page written before the field existed) reads as
          // owned, which is what every row in such a page actually was.
          isOwner: item.isOwner !== false,
          pinned: item.pinned === true,
          isNew: item.isNew === true,
          attention: attention
            .filter((receipt) => receipt.target.threadToken === item.token && receipt.acknowledgedAt === null)
            .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0],
        }
      })
  }, [data, running, attention])

  const loadMore = useCallback(async (): Promise<void> => {
    const current = pageRef.current
    if (!current?.nextCursor || loadingMoreRef.current) {
      return
    }
    loadingMoreRef.current = true
    setIsLoadingMore(true)
    try {
      const page = await recentConversationsApi.listPage({
        limit: SIDEBAR_THREADS_PAGE_SIZE,
        cursor: current.nextCursor,
      })
      await mutate(
        (cached) => {
          const loaded = cached?.items
          const cachedItems = Array.isArray(loaded) ? loaded : []
          return {
            items: mergeConversations(cachedItems, page.items ?? []),
            nextCursor: page.nextCursor,
          }
        },
        { revalidate: false },
      )
    } catch (error: unknown) {
      // A 400 means the stored cursor is no longer valid (the cursor format
      // changed): drop the accumulated pages and refetch page 1 once. Any
      // other failure surfaces as before.
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 400) {
        throw error
      }
      await mutate(undefined, { revalidate: true })
    } finally {
      loadingMoreRef.current = false
      setIsLoadingMore(false)
    }
  }, [mutate])

  async function renameThread(token: string, title: string): Promise<void> {
    const updated = await knowledgeThreadsApi.update(token, { title })
    await mutate(
      (current) =>
        mapSidebarThreadsCache(current, (cached) =>
          cached.map((thread) =>
            thread.token === token ? { ...thread, title: updated.title } : thread,
          ),
        ),
      { revalidate: false },
    )
  }

  async function pinThread(token: string, pinned: boolean): Promise<void> {
    const updated = await knowledgeThreadsApi.update(token, { pinned })
    await mutate(
      (current) =>
        mapSidebarThreadsCache(current, (cached) =>
          cached.map((thread) =>
            thread.token === token
              ? { ...thread, pinned: updated.pinned ?? pinned }
              : thread,
          ),
        ),
      { revalidate: false },
    )
  }

  async function deleteThread(token: string): Promise<void> {
    await knowledgeThreadsApi.delete(token)
    await mutate(
      (current) =>
        mapSidebarThreadsCache(current, (cached) =>
          cached.filter((thread) => thread.token !== token),
        ),
      { revalidate: false },
    )
  }

  async function renameRoom(token: string, title: string): Promise<void> {
    const updated = await chatRoomsApi.updateTitle(token, title)
    await mutate((current) => patchRoomInCache(current, token, { title: updated.title }), {
      revalidate: false,
    })
    revalidateConversationLists(mutateScopedCache, { includeRail: false })
  }

  async function deleteRoom(token: string): Promise<void> {
    await chatRoomsApi.delete(token)
    await mutate((current) => removeFromCache(current, 'room', token), { revalidate: false })
    revalidateConversationLists(mutateScopedCache, { includeRail: false })
  }

  async function pinListed(
    kind: 'room' | 'experiment' | 'draft',
    token: string,
    pinned: boolean,
  ): Promise<void> {
    await recentConversationsApi.setPinned({ kind, token, pinned })
    await mutate(
      (current) => {
        if (!current || !Array.isArray(current.items)) {
          return current
        }
        return {
          ...current,
          items: current.items.map((item) => {
            if (item.token !== token) {
              return item
            }
            if (kind === 'room') {
              return item.kind === 'room' ? { ...item, pinned } : item
            }
            return item.kind === 'study' && item.studyKind === kind ? { ...item, pinned } : item
          }),
        }
      },
      { revalidate: false },
    )
  }

  async function pinRoom(token: string, pinned: boolean): Promise<void> {
    await pinListed('room', token, pinned)
  }

  async function pinStudy(
    token: string,
    studyKind: RecentStudyItem['studyKind'],
    pinned: boolean,
  ): Promise<void> {
    await pinListed(studyKind, token, pinned)
  }

  return {
    threads,
    isLoading,
    isLoadingMore,
    hasMore: data?.nextCursor != null,
    loadMore,
    renameThread,
    pinThread,
    deleteThread,
    renameRoom,
    deleteRoom,
    pinRoom,
    pinStudy,
  }
}
